import React from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import 'i18n/config';

import RiskChart from 'pages/Strategy/RiskChart';
import Gauge from 'pages/Strategy/Gauge';
import { WhatIfResult } from './api';

type WhatIfPanelProps = {
  result: WhatIfResult | null;
  loading: boolean;
  onClose: () => void;
};

const Delta: React.FC<{ delta: number; suffix?: string; lowerIsBetter?: boolean }> = ({
  delta,
  suffix = '',
  lowerIsBetter = false,
}) => {
  if (!delta) return null;
  const improving = lowerIsBetter ? delta < 0 : delta > 0;
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{ fontWeight: 600, color: improving ? 'success.main' : 'warning.main' }}
    >
      {delta > 0 ? '+' : ''}
      {delta}
      {suffix}
    </Typography>
  );
};

const ChartCaption: React.FC<{
  before: React.ReactNode;
  after: React.ReactNode;
  delta: number;
  suffix?: string;
  lowerIsBetter?: boolean;
}> = ({ before, after, delta, suffix, lowerIsBetter }) => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={2} alignItems="baseline" justifyContent="center">
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('Current')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {before}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('Projected')}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {after}
        </Typography>
      </Stack>
      <Delta delta={delta} suffix={suffix} lowerIsBetter={lowerIsBetter} />
    </Stack>
  );
};

export const WhatIfPanel: React.FC<WhatIfPanelProps> = ({ result, loading, onClose }) => {
  const { t } = useTranslation();

  if (!result && !loading) return null;

  return (
    <Paper sx={{ marginTop: 1, marginBottom: 3, padding: 1, position: 'relative' }}>
      <IconButton
        size="small"
        aria-label={t('Clear selection')}
        title={t('Clear selection')}
        onClick={onClose}
        sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Box sx={{ margin: 1 }}>
        {loading && <LinearProgress sx={{ height: 3 }} />}
        {result && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Typography variant="paperTitle">{t('Selected period')}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                {moment(result.from).format('DD/MM/YYYY')} – {moment(result.to).format('DD/MM/YYYY')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {result.treatments} {t('treatments')} · {result.controls}{' '}
                {t('Controls').toLowerCase()}
              </Typography>

              <Typography variant="paperTitle">{t('Budget')}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {result.budget}
                <Typography component="span" variant="h6" sx={{ color: 'text.secondary', ml: 0.5 }}>
                  K €
                </Typography>
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="paperTitle">{t('Risk')}</Typography>
              <RiskChart
                key={`risk-${result.risk.after}`}
                name="whatIfRisk"
                value={result.risk.after}
                height="180px"
              />
              <ChartCaption
                before={result.risk.before}
                after={result.risk.after}
                delta={result.risk.delta}
                lowerIsBetter
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="paperTitle">{t('Coverage')}</Typography>
              <Gauge
                key={`coverage-${result.coverage.after}-${result.coverage.before}`}
                name="whatIfCoverage"
                value={result.coverage.after}
                backgroundValue={result.coverage.before}
                height="180px"
              />
              <ChartCaption
                before={`${result.coverage.before}%`}
                after={`${result.coverage.after}%`}
                delta={result.coverage.delta}
                suffix="%"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="paperTitle">{t('Maturity')}</Typography>
              <Gauge
                key={`maturity-${result.maturity.after}-${result.maturity.before}`}
                name="whatIfMaturity"
                value={result.maturity.after}
                backgroundValue={result.maturity.before}
                height="180px"
              />
              <ChartCaption
                before={`${result.maturity.before}%`}
                after={`${result.maturity.after}%`}
                delta={result.maturity.delta}
                suffix="%"
              />
            </Grid>
          </Grid>
        )}
        {result && (!result.target.coverage || !result.target.maturity) && (
          <Typography variant="body2" color="warning.main" sx={{ marginTop: 1 }}>
            {t('Set a target coverage and maturity in the Strategy page to estimate the risk reduction')}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
